import { useTheme } from "../../../context/ThemeContext";

type ServiceType = "installation" | "fix" | "replacement" | "detached";

interface TechnicalService {
  id: string;
  type: ServiceType;
  technician: string;
  client: string;
  unit: string;
  location: string;
  state: string;
  date: string;
  status: "scheduled" | "in_progress" | "completed";
  device: string;
}

const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  installation: "Instalación",
  fix: "Reparación",
  replacement: "Reemplazo",
  detached: "Desinstalación",
};

const SERVICE_TYPE_COLORS: Record<ServiceType, string> = {
  installation: "bg-primary-500",
  fix: "bg-yellow-500",
  replacement: "bg-blue-500",
  detached: "bg-red-500",
};

export default function LogisticaDashboard() {
  const { isDarkMode } = useTheme();

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-bg" : "bg-gray-50",
    cardBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    tableBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
  };

  const mockServices: TechnicalService[] = [
    {
      id: "ST-001",
      type: "installation",
      technician: "Carlos Mendoza",
      client: "Transportes del Norte",
      unit: "Kenworth T680 - ABC-123",
      location: "Monterrey",
      state: "Nuevo León",
      date: "28 Feb 2026, 09:00",
      status: "scheduled",
      device: "GPS Queclink GV300",
    },
    {
      id: "ST-002",
      type: "fix",
      technician: "Roberto García",
      client: "Logística Express",
      unit: "Freightliner Cascadia - XYZ-789",
      location: "Guadalajara",
      state: "Jalisco",
      date: "28 Feb 2026, 10:30",
      status: "in_progress",
      device: "GPS Teltonika FMC130",
    },
    {
      id: "ST-003",
      type: "replacement",
      technician: "Miguel Ángel López",
      client: "Fletes Rápidos SA",
      unit: "International LT - DEF-456",
      location: "Ciudad de México",
      state: "CDMX",
      date: "28 Feb 2026, 14:00",
      status: "scheduled",
      device: "Sensor de Combustible",
    },
    {
      id: "ST-004",
      type: "detached",
      technician: "José Hernández",
      client: "Carga Segura MX",
      unit: "Volvo VNL - GHI-321",
      location: "Tijuana",
      state: "Baja California",
      date: "27 Feb 2026, 16:00",
      status: "completed",
      device: "GPS Calamp LMU-3030",
    },
    {
      id: "ST-005",
      type: "installation",
      technician: "Fernando Ruiz",
      client: "Distribuidora Central",
      unit: "Peterbilt 579 - JKL-654",
      location: "Querétaro",
      state: "Querétaro",
      date: "28 Feb 2026, 11:00",
      status: "in_progress",
      device: "GPS + Sensor de Temperatura",
    },
    {
      id: "ST-006",
      type: "fix",
      technician: "Carlos Mendoza",
      client: "Mudanzas Express",
      unit: "Mercedes Actros - MNO-987",
      location: "León",
      state: "Guanajuato",
      date: "28 Feb 2026, 15:30",
      status: "scheduled",
      device: "GPS Queclink GV55",
    },
    {
      id: "ST-007",
      type: "installation",
      technician: "Roberto García",
      client: "Transporte Veloz",
      unit: "Mack Anthem - PQR-147",
      location: "Puebla",
      state: "Puebla",
      date: "01 Mar 2026, 09:00",
      status: "scheduled",
      device: "GPS + Sensor de Puertas",
    },
  ];

  const stats = [
    { label: "Servicios Hoy", value: "12", icon: "ri-calendar-todo-line", color: "text-blue-500" },
    { label: "En Progreso", value: "4", icon: "ri-loader-4-line", color: "text-yellow-500" },
    { label: "Completados", value: "8", icon: "ri-checkbox-circle-line", color: "text-primary-500" },
    { label: "Técnicos Activos", value: "6", icon: "ri-user-settings-line", color: "text-purple-500" },
  ];

  const servicesByType = [
    { type: "installation" as ServiceType, count: 15, percentage: 45 },
    { type: "fix" as ServiceType, count: 8, percentage: 24 },
    { type: "replacement" as ServiceType, count: 6, percentage: 18 },
    { type: "detached" as ServiceType, count: 4, percentage: 13 },
  ];

  const locationStats = [
    { state: "Nuevo León", city: "Monterrey", services: 5 },
    { state: "Jalisco", city: "Guadalajara", services: 4 },
    { state: "CDMX", city: "Ciudad de México", services: 3 },
    { state: "Querétaro", city: "Querétaro", services: 2 },
    { state: "Baja California", city: "Tijuana", services: 2 },
  ];

  const getStatusBadge = (status: TechnicalService["status"]) => {
    const styles = {
      scheduled: "bg-blue-500/20 text-blue-500",
      in_progress: "bg-yellow-500/20 text-yellow-500",
      completed: "bg-primary-500/20 text-primary-500",
    };
    const labels = {
      scheduled: "Programado",
      in_progress: "En Progreso",
      completed: "Completado",
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className={`w-full h-full p-6 overflow-auto ${themeClasses.bg}`}>
      <div className="mb-6">
        <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
          Dashboard de Logística - Servicios Técnicos
        </h1>
        <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
          Monitoreo de instalaciones, reparaciones y servicios de GPS y sensores
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>{stat.label}</p>
                <p className={`text-3xl font-bold ${themeClasses.text} mt-1`}>{stat.value}</p>
              </div>
              <i className={`${stat.icon} text-3xl ${stat.color}`} />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Services by Type */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4`}>
          <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
            Servicios por Tipo
          </h2>
          <div className="space-y-3">
            {servicesByType.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${SERVICE_TYPE_COLORS[item.type]}`} />
                    <span className={`text-sm ${themeClasses.text}`}>
                      {SERVICE_TYPE_LABELS[item.type]}
                    </span>
                  </div>
                  <span className={`text-sm ${themeClasses.textSecondary}`}>
                    {item.count} ({item.percentage}%)
                  </span>
                </div>
                <div className={`h-2 rounded-full ${isDarkMode ? "bg-dark-border" : "bg-gray-200"}`}>
                  <div
                    className={`h-full rounded-full ${SERVICE_TYPE_COLORS[item.type]}`}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Location Stats */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 lg:col-span-2`}>
          <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
            <i className="ri-map-pin-line mr-2 text-primary-500" />
            Servicios por Ubicación en México
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {locationStats.map((loc, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg ${themeClasses.tableBg}`}
              >
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>{loc.city}</p>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>{loc.state}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`text-2xl font-bold ${themeClasses.text}`}>{loc.services}</span>
                  <span className={`text-sm ${themeClasses.textSecondary}`}>servicios</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services Table */}
      <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4`}>
        <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
          Servicios Técnicos Recientes
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b ${themeClasses.cardBorder}`}>
                <th className={`text-left py-3 px-2 text-sm font-medium ${themeClasses.textSecondary}`}>ID</th>
                <th className={`text-left py-3 px-2 text-sm font-medium ${themeClasses.textSecondary}`}>Tipo</th>
                <th className={`text-left py-3 px-2 text-sm font-medium ${themeClasses.textSecondary}`}>Técnico</th>
                <th className={`text-left py-3 px-2 text-sm font-medium ${themeClasses.textSecondary}`}>Cliente</th>
                <th className={`text-left py-3 px-2 text-sm font-medium ${themeClasses.textSecondary}`}>Unidad</th>
                <th className={`text-left py-3 px-2 text-sm font-medium ${themeClasses.textSecondary}`}>Dispositivo</th>
                <th className={`text-left py-3 px-2 text-sm font-medium ${themeClasses.textSecondary}`}>Ubicación</th>
                <th className={`text-left py-3 px-2 text-sm font-medium ${themeClasses.textSecondary}`}>Fecha</th>
                <th className={`text-left py-3 px-2 text-sm font-medium ${themeClasses.textSecondary}`}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {mockServices.map((service) => (
                <tr
                  key={service.id}
                  className={`border-b ${themeClasses.cardBorder} hover:${themeClasses.tableBg}`}
                >
                  <td className={`py-3 px-2 text-sm ${themeClasses.text}`}>{service.id}</td>
                  <td className="py-3 px-2">
                    <span className={`text-xs px-2 py-1 rounded-full text-white ${SERVICE_TYPE_COLORS[service.type]}`}>
                      {SERVICE_TYPE_LABELS[service.type]}
                    </span>
                  </td>
                  <td className={`py-3 px-2 text-sm ${themeClasses.text}`}>{service.technician}</td>
                  <td className={`py-3 px-2 text-sm ${themeClasses.text}`}>{service.client}</td>
                  <td className={`py-3 px-2 text-sm ${themeClasses.textSecondary}`}>{service.unit}</td>
                  <td className={`py-3 px-2 text-sm ${themeClasses.textSecondary}`}>{service.device}</td>
                  <td className={`py-3 px-2 text-sm ${themeClasses.text}`}>
                    <div className="flex items-center">
                      <i className="ri-map-pin-line mr-1 text-primary-500" />
                      {service.location}, {service.state}
                    </div>
                  </td>
                  <td className={`py-3 px-2 text-sm ${themeClasses.textSecondary}`}>{service.date}</td>
                  <td className="py-3 px-2">{getStatusBadge(service.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
